<template>
  <span
    class="truncated-text"
    v-tooltip.top="tooltip"
    @mouseenter="measure"
    @touchstart.passive="measure"
  >
    <span v-if="head" ref="head" class="truncated-head" :style="cap">{{
      head
    }}</span>
    <span ref="tail" class="truncated-tail">{{ tailText }}</span>
  </span>
</template>

<script>
/**
 * The middle is dropped rather than the end: documents carry their extension there and folders
 * share prefixes, so the tail is what tells two names apart. Clipped by CSS rather than sliced,
 * so the full name stays in the DOM and survives select-and-copy.
 */
const MIN_TAIL = 4;

export default {
  name: "TruncatedText",
  props: {
    text: { type: String, default: "" },
    tail: { type: Number, default: 10 },
    max: { type: String, default: "" },
  },
  data() {
    return { isClipped: false };
  },
  computed: {
    tailLength() {
      return Math.max(MIN_TAIL, this.tail);
    },

    /* Empty for a short name, and the span is then dropped rather than rendered blank: an
       inline-flex takes its baseline from its first item, and an empty one is zero-height. */
    head() {
      return this.text.length > this.tailLength
        ? this.text.slice(0, -this.tailLength)
        : "";
    },

    tailText() {
      return this.text.length > this.tailLength
        ? this.text.slice(-this.tailLength)
        : this.text;
    },

    /* Definite, and on the head rather than the wrapper: an ancestor working out its own
       shrink-to-fit width ignores a percentage max-width, and sized itself to the whole name.
       The wrapper's `max-width: 100%` is what still lets it shrink below the cap. */
    cap() {
      return this.max
        ? { maxWidth: `max(0px, calc(${this.max} - ${this.tailLength}ch))` }
        : null;
    },

    /** Empty until measured, so a name that fits gets no tooltip repeating what is on screen. */
    tooltip() {
      return this.isClipped ? this.text : "";
    },
  },
  methods: {
    measure() {
      const el = this.$refs.head || this.$refs.tail;

      this.isClipped = !!el && el.scrollWidth > el.clientWidth + 1;
    },
  },
};
</script>

<style scoped>
/* Flex, so whitespace between the two spans cannot become a space inside the name. */
.truncated-text {
  display: inline-flex;
  max-width: 100%;
  min-width: 0;
}

.truncated-head {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre;
}

.truncated-tail {
  flex: 0 0 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre;
}
</style>
